import TaskCellStatus from "@/components/SingleLineTask/SingleLineTaskCells/taskCellStatus.component";
import { ROUTE_TASKS } from "@/constants/routes.constants";
import { Checkbox } from "@/shadcn/components/ui/checkbox";
import { BasicTask } from "@/types";
import {
  format,
  isPast,
  isThisYear,
  isToday,
  isTomorrow,
  isYesterday,
  parseISO,
} from "date-fns";
import React, { ReactNode } from "react";
import { useNavigate } from "react-router";

type SingleLineTaskMobileProps = {
  task: BasicTask;
  completeTask: (taskID: number) => void;
  taskType: "Overdue" | "Due Today" | "Due Later" | "No Due Date";
  inlineEditingDisabled?: boolean;
  isSelected?: boolean;
  onSelectChange?: (selected: boolean) => void;
};

const SingleLineTaskMobile: React.FC<SingleLineTaskMobileProps> = ({
  task,
  completeTask,
  taskType,
  inlineEditingDisabled = false,
  isSelected = false,
  onSelectChange,
}) => {
  const nav = useNavigate();

  function handleSingleClick() {
    if (inlineEditingDisabled) {
      return;
    }
    nav(ROUTE_TASKS + "/" + task.id);
  }

  function formatCellDate(date: string | null): ReactNode {
    if (!date) return <div className="">No Due Date</div>;

    const parsedDate = parseISO(date);
    let dateShown;
    let colorClass = "";
    if (isToday(parsedDate)) {
      colorClass = "text-green-400";
      dateShown = "Today";
    } else if (isTomorrow(parsedDate)) {
      colorClass = "text-indigo-400";
      dateShown = "Tomorrow";
    } else if (isPast(parsedDate)) {
      colorClass = "text-red-400";
      if (isYesterday(parsedDate)) {
        dateShown = "Yesterday";
      }
    }

    if (isThisYear(parsedDate)) {
      dateShown = dateShown ?? format(parsedDate, "yyy/MM/dd");
    } else {
      dateShown = dateShown ?? format(parsedDate, "yyy/MM/dd");
    }

    return <div className={`${colorClass}`}>{dateShown}</div>;
  }

  return (
    <div
      className={`grid grid-cols-14 items-center py-1 hover:bg-accent hover:text-accent-foreground border-l-4 h-12 select-none ${
        taskType === "Overdue"
          ? "border-red-300"
          : taskType === "Due Today"
            ? "border-green-300"
            : taskType === "Due Later"
              ? "border-indigo-300"
              : "border-gray-300"
      }`}
      onClick={handleSingleClick}
    >
      {/* Complete Task */}
      <div
        className={`flex items-center gap-2 col-span-2 ${
          onSelectChange ? "px-2" : ""
        }`}
      >
        {onSelectChange && (
          <Checkbox
            checked={isSelected}
            onCheckedChange={(value) => onSelectChange(!!value)}
            onMouseDown={(e) => {
              e.stopPropagation();
            }}
            onClick={(e) => {
              e.stopPropagation();
            }}
            aria-label="Select task"
          />
        )}
        <TaskCellStatus
          className="flex items-center"
          completed={task.status === "complete"}
          taskID={task.id}
          completeTask={completeTask}
          iconClassName={onSelectChange ? "ms-0" : undefined}
        />
      </div>
      {/* Title */}
      <div className="flex items-center justify-left col-span-9 text-sm">
        <div className="px-0 truncate">{task.title}</div>
      </div>
      {/* Due date */}
      <div className="flex items-center justify-left col-span-3 text-sm">
        <div className="px-0 truncate">
          {task.due_date ? formatCellDate(task.due_date) : "No Due Date"}
        </div>
      </div>
    </div>
  );
};

export default SingleLineTaskMobile;
